import { useState, useEffect } from 'react';
import { api, Organization, User, AdminRole, OrgProject } from '../api';
import { getErrorMessage } from '../../../shared/src';
import { StatusBadge } from './ui';
import { DeleteOrgModal } from './modals';
import { AuthMethodPanel } from './AuthMethodPanel';
import { SsoConfigPanel } from './SsoConfigPanel';
import { UsersTab } from './UsersTab';
import { UploadsTab } from './UploadsTab';
import { PermissionsTab } from './permissions/PermissionsTab';
import { ExtractionsTab } from './ExtractionsTab';

const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'app.fakto.ai';

type OrgTab = 'details' | 'auth' | 'sso' | 'users' | 'permissions' | 'uploads' | 'extractions';

interface OrganizationDetailProps {
  orgId: string;
  activeTab: OrgTab;
  activeSubtab: string;
  currentFolderId: string | null;
  selectedProjectId: string | null;
  onBack: () => void;
  onTabChange: (tab: OrgTab) => void;
  onSubtabChange: (subtab: string) => void;
  onFolderChange: (folderId: string | null) => void;
  onProjectChange: (projectId: string | null) => void;
  adminRole: AdminRole;
}

function LinkIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

export function OrganizationDetail({
  orgId,
  activeTab,
  activeSubtab,
  currentFolderId,
  selectedProjectId,
  onBack,
  onTabChange,
  onSubtabChange,
  onFolderChange,
  onProjectChange,
  adminRole,
}: OrganizationDetailProps) {
  const [org, setOrg] = useState<Organization | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingProject, setEditingProject] = useState<OrgProject | null>(null);

  const canEditProjects = adminRole !== 'admin_viewer';
  // Permissions tab is hidden from readers (admin_viewer); the API also blocks them.
  const canManagePermissions = adminRole !== 'admin_viewer';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [orgData, usersData] = await Promise.all([
          api.getOrganization(orgId),
          api.listOrgUsers(orgId),
        ]);
        setOrg(orgData);
        setUsers(usersData.users);
      } catch (err) {
        console.error('Failed to load organization:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgId]);

  useEffect(() => {
    if (activeTab !== 'uploads') return;
    const loadProjects = async () => {
      setProjectsLoading(true);
      try {
        const result = await api.listOrgProjects(orgId);
        setProjects(result.projects);
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setProjectsLoading(false);
      }
    };
    loadProjects();
  }, [orgId, activeTab]);

  const selectedProject = projects.find(p => p.id === selectedProjectId) ?? null;

  const handleCreateBucket = async () => {
    if (!org) return;
    setActionLoading(true);
    try {
      const result = await api.createBucket(org.id);
      setActionMessage({ type: 'success', text: `Bucket created: ${result.bucket.bucket_name}` });
      const updated = await api.getOrganization(org.id);
      setOrg(updated);
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to create bucket: ${getErrorMessage(err)}` });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePause = async () => {
    if (!org) return;
    setActionLoading(true);
    try {
      await api.pauseOrganization(org.id);
      const updated = await api.getOrganization(org.id);
      setOrg(updated);
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to pause: ${getErrorMessage(err)}` });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async () => {
    if (!org) return;
    setActionLoading(true);
    try {
      await api.reactivateOrganization(org.id);
      const updated = await api.getOrganization(org.id);
      setOrg(updated);
    } catch (err: unknown) {
      setActionMessage({ type: 'error', text: `Failed to reactivate: ${getErrorMessage(err)}` });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!org) return;
    await api.hardDeleteOrganization(org.id);
    setShowDeleteModal(false);
    onBack();
  };

  if (loading || !org) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <button className="btn-link" onClick={onBack}>&larr; Back</button>
          <h1>{org.name}</h1>
          <p className="subtitle">
            <code>{org.slug}.{APP_DOMAIN}</code>
            <StatusBadge status={org.status} />
          </p>
        </div>
        <div className="header-actions">
          {org.status === 'pending' && (
            <button className="btn-primary" onClick={handleCreateBucket} disabled={actionLoading}>
              {actionLoading ? 'Provisioning...' : 'Provision S3 Bucket'}
            </button>
          )}
          {org.status === 'active' && (
            <button className="btn-warning" onClick={handlePause} disabled={actionLoading}>
              {actionLoading ? 'Pausing...' : 'Pause Organization'}
            </button>
          )}
          {org.status === 'paused' && (
            <>
              <button className="btn-primary" onClick={handleReactivate} disabled={actionLoading}>
                {actionLoading ? 'Reactivating...' : 'Reactivate'}
              </button>
              <button className="btn-danger" onClick={() => setShowDeleteModal(true)} disabled={actionLoading}>
                Delete Permanently
              </button>
            </>
          )}
        </div>
      </header>

      {actionMessage && (
        <div className={actionMessage.type === 'success' ? 'success-message' : 'error-message'}>
          {actionMessage.text}
        </div>
      )}

      <div className="tabs">
        <button className={activeTab === 'details' ? 'active' : ''} onClick={() => onTabChange('details')}>
          Details
        </button>
        <button className={activeTab === 'auth' ? 'active' : ''} onClick={() => onTabChange('auth')}>
          Authentication
        </button>
        <button className={activeTab === 'sso' ? 'active' : ''} onClick={() => onTabChange('sso')}>
          SSO Configuration
        </button>
        <button className={activeTab === 'users' ? 'active' : ''} onClick={() => onTabChange('users')}>
          Users ({users.length})
        </button>
        {canManagePermissions && (
          <button className={activeTab === 'permissions' ? 'active' : ''} onClick={() => onTabChange('permissions')}>
            Permissions
          </button>
        )}
        <button className={activeTab === 'uploads' ? 'active' : ''} onClick={() => onTabChange('uploads')}>
          Uploads
        </button>
        <button className={activeTab === 'extractions' ? 'active' : ''} onClick={() => onTabChange('extractions')}>
          Extractions
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="tab-content">
          <div className="info-grid">
            <div className="info-item">
              <label>Organization ID</label>
              <code>{org.id}</code>
            </div>
            <div className="info-item">
              <label>S3 Bucket</label>
              <code>fakto-data-{org.org_id_hash}</code>
            </div>
            <div className="info-item">
              <label>Created</label>
              <span>{new Date(org.created_at).toLocaleString()}</span>
            </div>
            <div className="info-item">
              <label>Updated</label>
              <span>{new Date(org.updated_at).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'auth' && (
        <AuthMethodPanel org={org} onUpdate={(updated) => setOrg(updated)} />
      )}

      {activeTab === 'sso' && (
        <SsoConfigPanel org={org} onUpdate={(updated) => setOrg(updated)} />
      )}

      {activeTab === 'users' && (
        <UsersTab orgId={orgId} users={users} setUsers={setUsers} authMethod={org.auth_method || 'sso'} />
      )}

      {activeTab === 'permissions' && canManagePermissions && (
        <PermissionsTab
          orgId={orgId}
          activeSubtab={activeSubtab}
          onSubtabChange={onSubtabChange}
        />
      )}

      {activeTab === 'uploads' && (
        <div className="tab-content">
          {!selectedProject ? (
            <div className="project-selection">
              <h3>Select a Project</h3>
              <p className="project-selection-hint">Uploads are organised by project. Select one to browse its files.</p>
              {projectsLoading ? (
                <div className="loading">Loading projects...</div>
              ) : projects.length === 0 ? (
                <div className="empty-state">No projects found for this organisation.</div>
              ) : (
                <div className="project-cards">
                  {projects.map(project => (
                    <div key={project.id} className="project-card-wrapper">
                      <button
                        className="project-card"
                        onClick={() => onProjectChange(project.id)}
                      >
                        <span className="project-card-name">{project.name}</span>
                        <span className="project-card-slug">{project.slug}</span>
                        {project.external_ui_url && (
                          <span className="project-card-external-url" title={project.external_ui_url}>
                            ↗ {project.external_ui_url.replace(/^https:\/\//, '')}
                          </span>
                        )}
                      </button>
                      {canEditProjects && (
                        <button
                          type="button"
                          className="project-card-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProject(project);
                          }}
                          title="Edit external UI URL"
                          aria-label="Edit external UI URL"
                        >
                          <LinkIcon />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <UploadsTab
              orgId={orgId}
              projectId={selectedProject.id}
              currentFolderId={currentFolderId}
              onFolderChange={onFolderChange}
              projectRootFolderId={selectedProject.root_folder_id}
              projectName={selectedProject.name}
              onBackToProjects={() => onProjectChange(null)}
              adminRole={adminRole}
              availableProjects={projects}
            />
          )}
        </div>
      )}

      {activeTab === 'extractions' && (
        <ExtractionsTab orgId={orgId} orgSlug={org.slug} adminRole={adminRole} />
      )}

      {showDeleteModal && org && (
        <DeleteOrgModal
          org={org}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDelete}
        />
      )}

      {editingProject && (
        <EditProjectExternalUrlModal
          orgId={orgId}
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={(updated) => {
            setProjects(prev => prev.map(p => (p.id === updated.id ? updated : p)));
            setEditingProject(null);
          }}
        />
      )}
    </div>
  );
}

interface EditProjectExternalUrlModalProps {
  orgId: string;
  project: OrgProject;
  onClose: () => void;
  onSaved: (updated: OrgProject) => void;
}

// Modal-only component for setting projects.external_ui_url. Defence
// in depth: the API validates the regex server-side and the DB CHECK
// constraint validates again — we don't pretend to be authoritative
// here, we just show the regex as a hint so the operator gets fast
// feedback before submitting.
function EditProjectExternalUrlModal({
  orgId,
  project,
  onClose,
  onSaved,
}: EditProjectExternalUrlModalProps) {
  const [url, setUrl] = useState(project.external_ui_url ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const value = url.trim().length === 0 ? null : url.trim();
      const result = await api.updateOrgProject(orgId, project.id, {
        external_ui_url: value,
      });
      onSaved(result.project);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h2>Edit external UI URL — {project.name}</h2>
        <p className="modal-hint">
          When set, clicking this project in the org dashboard redirects
          the user to the URL instead of opening the in-app file browser.
          Must be HTTPS on a <code>.fakto.ai</code> host (enforced by the
          DB CHECK constraint and the frontend allowlist).
        </p>
        <label className="modal-field">
          <span>URL</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://nge-fuel.app.fakto.ai"
            disabled={saving}
          />
        </label>
        {error && <div className="modal-error">{error}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
